// dayjs
import dayjs from 'dayjs';

// redux
import { combineReducers, configureStore } from '@reduxjs/toolkit';

// redux-persist
import { persistStore, persistReducer, createMigrate, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
const storage = {
  getItem: (key) => Promise.resolve(localStorage.getItem(key)),
  setItem: (key, value) => Promise.resolve(localStorage.setItem(key, value)),
  removeItem: (key) => Promise.resolve(localStorage.removeItem(key))
};

// sentry
import * as Sentry from '@sentry/react';

// reducers
import customizationReducer from './features/customizationSlice';
import userReducer, { initialState as initialStateUser } from './features/userSlice';
import respondentReducer from './features/respondentSlice';
import appConfigReducer, { initialState as initialStateAppConfig } from './features/appConfigSlice';
import appDataSourceReducer, { initialState as initialStateAppDataSource } from './features/appDataSourceSlice';
import snackbarReducer from './features/snackbarSlice';
import calendarFiltersReducer, { initialState as initialStateCalendar } from './features/calendarFiltersSlice';
import alertReducer from './features/alertSlice';
import triggerAlertBannerReducer from './features/triggerAlertBannerSlice';
import appReducer from './features/appSlice';
import dashboardActionReducer from './features/dashboardActionSlice';

// constant
import { calendarModes } from 'constants/calendar';
import { CALENDAR_LOCATION_LIMIT, CALENDAR_MANAGER_LIMIT } from '@a1/shared';

// ==============================|| REDUX - MAIN STORE ||============================== //

// items to reset when a redux reset is requested i.e. on logout
const resetSlices = ['customization', 'respondent', 'user', 'triggerAlertBanner'];

// items to persist.
// persisting items will allow them to work between multiple tabs, or across multiple sessions
// ensure any items you do not want to persist after a session is complete are added to the resetSlices above
const persistSlices = ['app', 'appConfig', 'appDataSource', 'calendarFilters', 'user'];

// redux persist migrations
// https://github.com/rt2zz/redux-persist/blob/master/docs/migrations.md
const migrations = {
  // initial migration
  0: (state) => ({
    ...state,
    // reset all persisted slices
    user: initialStateUser,
    appConfig: initialStateAppConfig,
    calendarFilters: initialStateCalendar
  }),
  // add more migrations as needed
  // ...
  1: (state) => ({
    ...state,
    calendarFilters: {
      ...state.calendarFilters,
      calendarManagerFilter: []
    }
  }),
  2: (state) => ({
    ...state,
    calendarFilters: {
      ...state.calendarFilters,
      calendarModeFilter: calendarModes.ByLocation
    }
  }),
  3: (state) => ({
    ...state,
    calendarFilters: {
      ...state.calendarFilters,
      calendarModeFilter: calendarModes.ByLocation,
      calendarMinTimeFilter: state.calendarFilters.calendarMinTimeFilter
        ? dayjs(state.calendarFilters.calendarMinTimeFilter).format('HH:mm')
        : null,
      calendarMaxTimeFilter: state.calendarFilters.calendarMinTimeFilter
        ? dayjs(state.calendarFilters.calendarMaxTimeFilter).format('HH:mm')
        : null
    }
  }),
  4: (state) => ({
    ...state,
    calendarFilters: {
      ...state.calendarFilters,
      calendarCondensedView: false
    }
  }),
  5: (state) => ({
    ...state,
    appConfig: {
      ...state.appConfig,
      data: {
        ...state.appConfig.data,
        /* eslint-disable no-unused-vars */
        // remove absence related app config values, this is an IIFE btw if it looks confusing
        entities: (({ absence, absences, absenceManagement, ...rest }) => rest)(state?.appConfig?.data?.entities || {}),
        others: (({ absenceManagement, ...rest }) => rest)(state?.appConfig?.data?.others || {})
        /* eslint-enable no-unused-vars */
      }
    }
  }),
  6: (state) => ({
    ...state,
    appDataSource: initialStateAppDataSource
  }),
  7: (state) => ({
    ...state,
    calendarFilters: {
      ...state.calendarFilters,
      calendarLocationFilter: Array.isArray(state.calendarFilters.calendarLocationFilter)
        ? [...state.calendarFilters.calendarLocationFilter].slice(0, CALENDAR_LOCATION_LIMIT)
        : [],
      calendarManagerFilter: Array.isArray(state.calendarFilters.calendarManagerFilter)
        ? [...state.calendarFilters.calendarManagerFilter].slice(0, CALENDAR_MANAGER_LIMIT)
        : []
    }
  })
};

const persistConfig = {
  key: 'root',
  // set version to latest migration number
  version: 7,
  migrate: createMigrate(migrations, { debug: false }),
  // save to local storage, other storage options https://github.com/rt2zz/redux-persist#storage-engines
  storage,
  whitelist: persistSlices
};

const rootReducer = combineReducers({
  customization: customizationReducer,
  user: userReducer,
  respondent: respondentReducer,
  snackbar: snackbarReducer,
  appConfig: appConfigReducer,
  appDataSource: appDataSourceReducer,
  calendarFilters: calendarFiltersReducer,
  alert: alertReducer,
  triggerAlertBanner: triggerAlertBannerReducer,
  app: appReducer,
  dashboardAction: dashboardActionReducer
});

const resetActionReducer = (state, action) => {
  if (action.type === 'RESET_APP') {
    for (const sliceName of resetSlices) {
      state[sliceName] = undefined;
    }
  }
  return rootReducer(state, action);
};

// enhanced reducer with configuration to persist the state
// https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
const persistedReducer = persistReducer(persistConfig, resetActionReducer);

// sentry
const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }),
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer)
});

const persistor = persistStore(store);

export { store, persistor };
